import {
   Section,
   StructuredQuestion,
   StructuredSurveyTemplate,
   VisibilityRule,
} from '@dreryk/egabinet-survey-frontend-api';

import { VisibilityResolver } from './MedicalProfileTab.types';

/*
 * TODO DREGAB-29796: the backend does not describe how a gate reveals its details (no visibility rule has a gate as its
 * source) - "details are shown after answering YES on the gate" is a frontend assumption, to be confirmed with the backend.
 * Only the main gate of a row is special: backend confirmed that gates nested in sections are a mistake and everything
 * below the main gate is driven by visibility rules.
 */
export const GATE_YES_CODE = 'YES';

const MAX_RULE_DEPTH = 50;

type TemplateIndex = {
   readonly questions: Map<number, StructuredQuestion>;
   readonly sections: Map<number, Section>;
   readonly ruleSourceQuestionIds: Set<number>;
   readonly rulesByTargetQuestionId: Map<number, VisibilityRule[]>;
   readonly rulesByTargetSectionId: Map<number, VisibilityRule[]>;
};

const templateIndexCache = new WeakMap<StructuredSurveyTemplate, TemplateIndex>();

export const isGateQuestion = (question: StructuredQuestion): boolean =>
   question.gate && question.answerType === 'SINGLE_CHOICE';

const addRule = (rulesByTarget: Map<number, VisibilityRule[]>, targetId: number, rule: VisibilityRule): void => {
   rulesByTarget.set(targetId, [...(rulesByTarget.get(targetId) ?? []), rule]);
};

const buildTemplateIndex = (template: StructuredSurveyTemplate): TemplateIndex => {
   const index: TemplateIndex = {
      questions: new Map(template.questions.map((question) => [question.id, question])),
      sections: new Map(template.sections.map((section) => [section.id, section])),
      ruleSourceQuestionIds: new Set(template.visibilityRules.map((rule) => rule.sourceQuestionId)),
      rulesByTargetQuestionId: new Map(),
      rulesByTargetSectionId: new Map(),
   };
   template.visibilityRules.forEach((rule) => {
      if (rule.targetQuestionId !== undefined && rule.targetQuestionId !== null) {
         addRule(index.rulesByTargetQuestionId, rule.targetQuestionId, rule);
      }
      if (rule.targetSectionId !== undefined && rule.targetSectionId !== null) {
         addRule(index.rulesByTargetSectionId, rule.targetSectionId, rule);
      }
   });
   return index;
};

const getTemplateIndex = (template: StructuredSurveyTemplate): TemplateIndex => {
   const cached = templateIndexCache.get(template);
   if (cached) {
      return cached;
   }
   const index = buildTemplateIndex(template);
   templateIndexCache.set(template, index);
   return index;
};

const matchesRule = (rule: VisibilityRule, answerCodes: string[]): boolean => {
   const hasOperandCode = rule.operand.some((code) => answerCodes.includes(code));
   return rule.operator === 'NOT_IN' ? !hasOperandCode : hasOperandCode;
};

type InternalResolver = {
   readonly isSectionVisible: (sectionId: number, depth: number) => boolean;
   readonly isQuestionVisible: (questionId: number, depth: number) => boolean;
   readonly isAnyRuleSatisfied: (rules: VisibilityRule[], depth: number) => boolean;
};

export const createVisibilityResolver = (
   template: StructuredSurveyTemplate,
   getAnswer: (questionId: number) => string[],
   openSectionId?: number
): VisibilityResolver => {
   const index = getTemplateIndex(template);

   const resolver: InternalResolver = {
      isSectionVisible: (sectionId, depth) => {
         if (depth > MAX_RULE_DEPTH) {
            return false;
         }
         if (sectionId === openSectionId) {
            return true;
         }
         const rules = index.rulesByTargetSectionId.get(sectionId);
         if (rules) {
            return resolver.isAnyRuleSatisfied(rules, depth);
         }
         const parentQuestionId = index.sections.get(sectionId)?.parentQuestionId;
         if (parentQuestionId === undefined || parentQuestionId === null) {
            return true;
         }
         const parentQuestion = index.questions.get(parentQuestionId);
         if (!parentQuestion || !isGateQuestion(parentQuestion)) {
            return false;
         }
         return (
            resolver.isQuestionVisible(parentQuestionId, depth + 1) &&
            getAnswer(parentQuestionId).includes(GATE_YES_CODE)
         );
      },
      isQuestionVisible: (questionId, depth) => {
         if (depth > MAX_RULE_DEPTH) {
            return false;
         }
         const question = index.questions.get(questionId);
         if (!question) {
            return false;
         }
         if (
            question.sectionId !== undefined &&
            question.sectionId !== null &&
            !resolver.isSectionVisible(question.sectionId, depth + 1)
         ) {
            return false;
         }
         const rules = index.rulesByTargetQuestionId.get(questionId);
         return rules ? resolver.isAnyRuleSatisfied(rules, depth) : true;
      },
      isAnyRuleSatisfied: (rules, depth) =>
         rules.some(
            (rule) =>
               resolver.isQuestionVisible(rule.sourceQuestionId, depth + 1) &&
               matchesRule(rule, getAnswer(rule.sourceQuestionId))
         ),
   };

   return {
      isSectionVisible: (sectionId) => resolver.isSectionVisible(sectionId, 0),
      isQuestionVisible: (questionId) => resolver.isQuestionVisible(questionId, 0),
   };
};

export const isVisibilityControlQuestion = (
   template: StructuredSurveyTemplate,
   question: StructuredQuestion
): boolean => isGateQuestion(question) || getTemplateIndex(template).ruleSourceQuestionIds.has(question.id);
